import * as THREE from 'three';
import { resolveCircleWalls, circleVsCircle } from '../engine/collision.js';
import { prepareModel } from '../engine/loader.js';
import { getWeaponById, STARTER_WEAPON_IDS, WEAPON_CATEGORY, RARITY_ORDER } from './weaponRuntime.js';

// Player: loads FBX when available, falls back to procedural voxel body.
// Movement in world XZ plane; aim rotates upper body toward cursor.

const PLAYER_BASE_SPEED = 7.0;
const RUN_SPEED_MULT = 1.55;
const PLAYER_RADIUS = 0.55;
const DASH_SPEED = 22;
const DASH_TIME = 0.16;
const DASH_CD_BASE = 1.4;
const STAMINA_MAX = 1;
const STAMINA_DASH_COST = STAMINA_MAX;
const STAMINA_RECOVERY_PER_SEC = STAMINA_MAX / DASH_CD_BASE;
const STAMINA_RUN_DRAIN_PER_SEC = STAMINA_MAX * 0.5;
const MAX_HP = 8;
const STARTER_MEDKITS = 3;
const MEDKIT_HEAL_AMOUNT = 3;
const HEAL_BLINK_DURATION = 0.22;
const BASE_MELEE_REACH = 1.7;
const ECHO_RING_BULLET_SPEED = 14;
const ACTIVE_KEY = 'KeyQ';
const FALLBACK_STARTER_RARITY = 'common';
const DEATH_ANIM_DURATION = 0.6;
const DEATH_TOTAL_DURATION = 1.0;
const DEATH_BLOOD_LIFETIME = 0.8;
const SHOOT_ANIM_HOLD = 0.26;

export class Player {
  constructor({ scene, fbx, audio }) {
    this.scene = scene;
    this.audio = audio;
    this.fbx = fbx;

    this.group = new THREE.Group();
    this.x = 0;
    this.z = 0;
    this.vx = 0;
    this.vz = 0;
    this.aimX = 1;
    this.aimZ = 0;
    this.radius = PLAYER_RADIUS;
    this.hp = MAX_HP;
    this.maxHp = MAX_HP;
    this.medkits = STARTER_MEDKITS;
    this.alive = true;

    this.dashCd = 0;
    this.dashTime = 0;
    this.dashDirX = 0;
    this.dashDirZ = 0;
    this.invuln = 0;
    this.stamina = STAMINA_MAX;

    this.fireCd = 0;
    this.hands = {
      left: this._createHandState(STARTER_WEAPON_IDS[0], FALLBACK_STARTER_RARITY),
      right: this._createHandState(STARTER_WEAPON_IDS[1], FALLBACK_STARTER_RARITY),
    };
    this.swapHintShown = false;
    this.infiniteAmmo = false;
    this._preferredHudHand = 'right';
    this._standTime = 0;
    this._cloaked = false;
    this._justUncloaked = false;
    this._debugHidden = false;

    this.mixer = null;
    this.actions = {};
    this._currentAction = null;
    this._model = null;
    this._upperBody = null; // rotates toward aim
    this._idleAction = null;
    this._runForwardAction = null;
    this._runBackwardAction = null;
    this._runLeftAction = null;
    this._runRightAction = null;
    this._shootAction = null;
    this._shootAnimTimer = 0;

    this.fragments = new Set();
    this.fragmentState = {};
    this.shotCounter = 0;

    this.speedMul = 1;
    this.fireRateMul = 1;
    this.dashCdMul = 1;
    this.dashIframeMul = 1;
    this.nextShotCritMul = 1;

    this._tmpVec = new THREE.Vector3();
    this._footstepDistance = 0;
    this._meleeFaceTimer = 0;
    this.characterId = 'kira';
    this._characterProfile = null;
    this._activeCd = 0;
    this._activeTime = 0;
    this._roomArmorReady = false;
    this._lastShotAt = -999;
    this._markedEnemy = null;
    this._vanishBreakBonus = 1;
    this._healBlinkTimer = 0;
    this._deathSequence = {
      active: false,
      elapsed: 0,
      animDone: false,
      startScale: new THREE.Vector3(1, 1, 1),
      startRotX: 0,
      tiltDir: 1,
      bloodParticles: [],
    };

    this._buildVisual();
    this._buildLight();
    scene.add(this.group);
  }

  _createHandState(weaponId, rarityOverride = null) {
    const safeRarity = this._normalizeRarity(rarityOverride);
    const weapon = getWeaponById(weaponId, { rarityOverride: safeRarity })
      || getWeaponById(STARTER_WEAPON_IDS[0], { rarityOverride: safeRarity });
    const isMelee = weapon.category === WEAPON_CATEGORY.MELEE;
    return {
      weaponId: weapon.id,
      rarityOverride: safeRarity,
      ammoInMag: isMelee ? Number.POSITIVE_INFINITY : weapon.runtime.magSize,
      ammoReserve: isMelee ? Number.POSITIVE_INFINITY : weapon.runtime.reserve,
      magCapacity: isMelee ? Number.POSITIVE_INFINITY : weapon.runtime.magSize,
      reserveCapacity: isMelee ? Number.POSITIVE_INFINITY : weapon.runtime.reserve,
      reloadDuration: isMelee ? 0 : weapon.runtime.reloadDuration,
      reloadTime: 0,
      fireCd: 0,
      triggerHeld: false,
      shotsSinceTrigger: 0,
    };
  }

  _normalizeRarity(rarity) {
    if (!rarity) return null;
    const safe = String(rarity).toLowerCase();
    return RARITY_ORDER.includes(safe) ? safe : null;
  }

  _normalizeStarterLoadout(profile) {
    const loadout = Array.isArray(profile?.starterLoadout) ? profile.starterLoadout.filter(Boolean) : [];
    const left = loadout[0] || STARTER_WEAPON_IDS[0];
    const right = loadout[1] || loadout[0] || STARTER_WEAPON_IDS[1] || STARTER_WEAPON_IDS[0];
    const rarity = this._normalizeRarity(profile?.starterRarity)
      || (profile?.id === 'esme' || profile?.id === 'rook' ? 'rare' : 'uncommon');
    return { left, right, rarity };
  }

  _applyStarterLoadout(profile) {
    const starter = this._normalizeStarterLoadout(profile);
    this.hands.left = this._createHandState(starter.left, starter.rarity);
    this.hands.right = this._createHandState(starter.right, starter.rarity);
  }

  _buildLight() {
    // Warm personal light source.
    //
    // `lightRadius` is the GAMEPLAY radius (2m in the XZ plane) used by
    // `Game.illuminationAt` to dim vision cones / bullets. The 3D PointLight
    // `distance` must be larger than this because the light sits above the
    // floor — from a light at y=0.9, reaching a point 2m away on the floor
    // is a 3D distance of sqrt(0.9^2 + 2^2) ≈ 2.2m, so we use ~3.2m so the
    // full 2m floor disc actually receives illumination. A gentler decay
    // than the physical 2.0 keeps the bubble readable without having to
    // crank intensity into the hundreds.
    this.lightRadius = 200;
    // Saturated warm-orange pool so the player reads as a strong warm pocket
    // against the scene's cool cyan fill (cinematic teal/orange contrast).
    const light = new THREE.PointLight(0x07e489, 50, 18, 0.4);
    light.position.set(0, 0.9, 0);
    light.castShadow = false;
    this.group.add(light);
    this._light = light;
  }

  _buildVisual() {
    if (this.fbx) {
      // Use the original loaded model instance for skinned animation reliability.
      // `clone(true)` is not safe for all skinned rigs and can leave clips unbound.
      const model = prepareModel(this.fbx);
      // Normalize scale: FBX often comes in cm (100 = 1m)
      const box = new THREE.Box3().setFromObject(model);
      const size = new THREE.Vector3();
      box.getSize(size);
      const targetHeight = 1.7;
      const s = size.y > 0.01 ? targetHeight / size.y : 1;
      model.scale.setScalar(s);
      model.position.y = 0;
      this._model = model;
      this.group.add(model);

      if (this.fbx.animations && this.fbx.animations.length) {
        this.mixer = new THREE.AnimationMixer(model);
        for (const clip of this.fbx.animations) {
          const action = this.mixer.clipAction(clip);
          action.loop = THREE.LoopRepeat;
          this.actions[clip.name.toLowerCase()] = action;
        }
        this.mixer.stopAllAction();
        this._resolveDirectionalActions();
        this._playAction(this._idleAction ?? Object.values(this.actions)[0]);
      }
    } else {
      this._buildProceduralVoxelPlayer();
    }
  }

  _buildProceduralVoxelPlayer() {
    const body = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({ color: 0x1a2838, roughness: 0.85, metalness: 0.1 });
    const matAcc = new THREE.MeshStandardMaterial({ color: 0x8afff0, emissive: 0x4a9c94, emissiveIntensity: 0.6 });
    const matHead = new THREE.MeshStandardMaterial({ color: 0xe0c090, roughness: 0.9 });

    const torso = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.9, 0.5), mat);
    torso.position.y = 1.05;
    torso.castShadow = true;
    body.add(torso);

    const head = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.55, 0.55), matHead);
    head.position.y = 1.78;
    head.castShadow = true;
    body.add(head);

    const visor = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.12, 0.06), matAcc);
    visor.position.set(0, 1.82, 0.28);
    body.add(visor);

    const legL = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.6, 0.4), mat);
    legL.position.set(-0.22, 0.3, 0); legL.castShadow = true; body.add(legL);
    const legR = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.6, 0.4), mat);
    legR.position.set(0.22, 0.3, 0); legR.castShadow = true; body.add(legR);

    const upper = new THREE.Group();
    upper.position.y = 1.05;
    body.remove(torso);
    torso.position.y = 0;
    upper.add(torso);
    const armL = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.7, 0.24), mat);
    armL.position.set(-0.55, 0.05, 0); armL.castShadow = true; upper.add(armL);
    const armR = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.7, 0.24), mat);
    armR.position.set(0.55, 0.05, 0.15); armR.castShadow = true; upper.add(armR);
    const gun = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.18, 0.7), new THREE.MeshStandardMaterial({ color: 0x0f1318, roughness: 0.4, metalness: 0.7 }));
    gun.position.set(0.55, 0.05, 0.55);
    upper.add(gun);
    body.add(upper);

    this._upperBody = upper;
    this._muzzleOffset = new THREE.Vector3(0.55, 1.15, 0.95);

    this.group.add(body);
  }

  _findAction(candidates) {
    for (const n of candidates) {
      for (const key of Object.keys(this.actions)) {
        if (key.includes(n)) return this.actions[key];
      }
    }
    return null;
  }

  _playAction(action, fade = 0.18) {
    if (!action || this._currentAction === action) return;
    if (this._currentAction) this._currentAction.fadeOut(fade);
    action.enabled = true;
    action.paused = false;
    action.setEffectiveWeight(1);
    action.setEffectiveTimeScale(1);
    action.reset().fadeIn(fade).play();
    this._currentAction = action;
  }

  _triggerShootAnimation() {
    if (!this._shootAction) return;
    const fade = 0.05;
    if (this._currentAction && this._currentAction !== this._shootAction) {
      this._currentAction.fadeOut(fade);
    }
    // Force restart each successful shot, even when shoot action is already active.
    this._shootAction.enabled = true;
    this._shootAction.paused = false;
    this._shootAction.setEffectiveWeight(1);
    this._shootAction.setEffectiveTimeScale(1);
    this._shootAction.reset().fadeIn(fade).play();
    this._currentAction = this._shootAction;
    this._shootAnimTimer = SHOOT_ANIM_HOLD;
  }

  _resolveDirectionalActions() {
    this._idleAction = this._findAction(['idle', 'breathing', 'tpose']);
    this._runForwardAction = this._findAction(['run_forward', 'run forward', 'forward']);
    this._runBackwardAction = this._findAction(['run_backward', 'run backward', 'backward', 'back']);
    this._runLeftAction = this._findAction(['run_left', 'run left', 'strafe_left', 'left']);
    this._runRightAction = this._findAction(['run_right', 'run right', 'strafe_right', 'right']);
    this._shootAction = this._findAction(['shoot', 'fire', 'attack']);
    if (this._shootAction) {
      this._shootAction.loop = THREE.LoopOnce;
      this._shootAction.clampWhenFinished = false;
    }
  }

  _pickDirectionalRunAction() {
    const moveLen = Math.hypot(this.vx, this.vz);
    if (moveLen < 0.05) return null;

    // Resolve movement relative to aim to select strafe/backpedal animations.
    const moveX = this.vx / moveLen;
    const moveZ = this.vz / moveLen;
    const forward = { x: this.aimX, z: this.aimZ };
    const right = { x: forward.z, z: -forward.x };

    const forwardDot = moveX * forward.x + moveZ * forward.z;
    const rightDot = moveX * right.x + moveZ * right.z;

    if (Math.abs(forwardDot) >= Math.abs(rightDot)) {
      if (forwardDot >= 0) return this._runForwardAction || this._runRightAction || this._runLeftAction;
      return this._runBackwardAction || this._runForwardAction;
    }
    if (rightDot >= 0) return this._runRightAction || this._runForwardAction;
    return this._runLeftAction || this._runForwardAction;
  }

  hasFragment(id) { return this.fragments.has(id); }

  applyFragment(id) {
    if (this.fragments.has(id)) return;
    this.fragments.add(id);
    if (id === 'phantom_step') { this.dashCdMul *= 0.5; this.dashIframeMul *= 1.5; }
  }
  applyCharacterProfile(profile) {
    if (!profile) return;
    this._characterProfile = profile;
    this.characterId = profile.id || 'kira';
    this.maxHp = Math.max(4, Number(profile.stats?.maxHp) || MAX_HP);
    this.hp = Math.min(this.maxHp, this.hp);
    this.speedMul = Number(profile.stats?.speedMul) || 1;
    this.fireRateMul = Number(profile.stats?.fireRateMul) || 1;
    this._damageMul = Number(profile.stats?.damageMul) || 1;
    this._roomArmorReady = this.characterId === 'dax';
    this._activeCd = 0;
    this._activeTime = 0;
    this._markedEnemy = null;
    this._vanishBreakBonus = 1;
    this._applyStarterLoadout(profile);
  }

  heal(n = 1) {
    this.hp = Math.min(this.maxHp, this.hp + n);
  }

  useMedkit() {
    if (!this.alive || this.medkits <= 0 || this.hp >= this.maxHp) return false;
    this.medkits -= 1;
    const before = this.hp;
    this.heal(MEDKIT_HEAL_AMOUNT);
    if (this.hp > before) {
      this._healBlinkTimer = HEAL_BLINK_DURATION;
      return true;
    }
    return false;
  }

  takeDamage(n, game) {
    if (!this.alive || this.invuln > 0) return;
    if (this.characterId === 'dax' && this._roomArmorReady) {
      this._roomArmorReady = false;
      game?.toast?.('Armor Plating absorbed hit');
      return;
    }
    if (this.characterId === 'dax' && this._activeTime > 0) {
      n *= 0.3;
    }
    this.hp -= n;
    this.invuln = 0.5;
    if (this.audio) this.audio.play('hit', { pitchJitter: 0.15 });
    game?.onPlayerHit(n);
    if (this.hp <= 0) {
      this.hp = 0;
      this.alive = false;
      this.vx = 0;
      this.vz = 0;
      this.dashTime = 0;
      this.dashDirX = 0;
      this.dashDirZ = 0;
      this._startDeathSequence(game);
      if (this.audio) this.audio.play('playerTerminated');
      game?.onPlayerDeath();
    }
  }

  _startDeathSequence(game) {
    const seq = this._deathSequence;
    seq.active = true;
    seq.elapsed = 0;
    seq.animDone = false;
    seq.startScale.copy(this.group.scale);
    seq.startRotX = this.group.rotation.x;
    seq.tiltDir = Math.random() < 0.5 ? -1 : 1;
    this.group.scale.set(1, 1, 1);
    this._clearDeathBloodParticles();
    this._spawnDeathBloodParticles(game);
  }

  _spawnDeathBloodParticles(game) {
    const count = 6 + Math.floor(Math.random() * 5); // 6-10 particles
    const scene = game?.scene || this.scene;
    if (!scene) return;
    const particles = [];
    for (let i = 0; i < count; i++) {
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(0.09, 0.09, 0.09),
        new THREE.MeshStandardMaterial({
          color: 0x8c1016,
          emissive: 0x260204,
          emissiveIntensity: 0.4,
          transparent: true,
          opacity: 0.95,
          roughness: 0.75,
          metalness: 0.06,
          depthWrite: false,
        })
      );
      const angle = Math.random() * Math.PI * 2;
      const speed = 1.8 + Math.random() * 2.4;
      const up = 1.1 + Math.random() * 1.4;
      const lift = 0.15 + Math.random() * 0.35;
      mesh.position.set(this.x, lift, this.z);
      scene.add(mesh);
      particles.push({
        mesh,
        vx: Math.cos(angle) * speed,
        vz: Math.sin(angle) * speed,
        vy: up,
        age: 0,
        life: DEATH_BLOOD_LIFETIME,
      });
    }
    this._deathSequence.bloodParticles = particles;
  }

  _updateDeathSequence(dt) {
    const seq = this._deathSequence;
    if (!seq.active) return;
    seq.elapsed += dt;

    const t = Math.min(1, seq.elapsed / DEATH_ANIM_DURATION);
    // Ease-out slump/fall with a soft ground squash.
    const eased = 1 - Math.pow(1 - t, 3);
    this.group.rotation.x = seq.startRotX + seq.tiltDir * (Math.PI * 0.5) * eased;
    const sx = THREE.MathUtils.lerp(seq.startScale.x, seq.startScale.x * 1.06, eased);
    const sy = THREE.MathUtils.lerp(seq.startScale.y, seq.startScale.y * 0.2, eased);
    const sz = THREE.MathUtils.lerp(seq.startScale.z, seq.startScale.z * 1.06, eased);
    this.group.scale.set(sx, sy, sz);
    if (this.group.position.y !== 0) this.group.position.y = 0;
    if (!seq.animDone && t >= 1) seq.animDone = true;

    const gravity = 6.5;
    for (let i = seq.bloodParticles.length - 1; i >= 0; i--) {
      const p = seq.bloodParticles[i];
      p.age += dt;
      p.vy -= gravity * dt;
      p.mesh.position.x += p.vx * dt;
      p.mesh.position.y = Math.max(0.03, p.mesh.position.y + p.vy * dt);
      p.mesh.position.z += p.vz * dt;
      const lifeT = Math.min(1, p.age / p.life);
      p.mesh.material.opacity = 0.95 * (1 - lifeT);
      if (lifeT >= 1) {
        p.mesh.parent?.remove(p.mesh);
        p.mesh.geometry?.dispose?.();
        p.mesh.material?.dispose?.();
        seq.bloodParticles.splice(i, 1);
      }
    }
  }

  _clearDeathBloodParticles() {
    const particles = this._deathSequence?.bloodParticles || [];
    for (const p of particles) {
      p.mesh?.parent?.remove?.(p.mesh);
      p.mesh?.geometry?.dispose?.();
      p.mesh?.material?.dispose?.();
    }
    if (this._deathSequence) this._deathSequence.bloodParticles = [];
  }

  _weaponCooldown(weapon) {
    let cd = weapon.runtime.cooldown / this.fireRateMul;
    if (this.hasFragment('null_overdrive') && this.hp / this.maxHp <= 0.25) cd /= 1.4;
    if (this.characterId === 'yael') {
      const overclockStacks = Math.min(3, this.fragments.size);
      cd /= 1 + overclockStacks * 0.05;
    }
    return cd;
  }

  moveSpeed(isRunning = false) {
    let s = PLAYER_BASE_SPEED * this.speedMul;
    if (this.hasFragment('null_overdrive') && this.hp / this.maxHp <= 0.25) s *= 1.2;
    if (isRunning) s *= RUN_SPEED_MULT;
    if (this.characterId === 'dax' && this._activeTime > 0) s *= 0.45;
    return s;
  }

  getHandState(hand) {
    return this.hands[hand] || this.hands.right;
  }

  getHandWeapon(hand) {
    const state = this.getHandState(hand);
    return getWeaponById(state.weaponId, { rarityOverride: state.rarityOverride });
  }

  getHandHudState(hand) {
    const state = this.getHandState(hand);
    const weapon = this.getHandWeapon(hand);
    return {
      hand,
      weaponId: state.weaponId,
      weaponName: weapon?.name || 'Unknown',
      rarity: weapon?.rarity || 'common',
      category: weapon?.category || WEAPON_CATEGORY.CLOSE_RANGE,
      ammoInMag: Number.isFinite(state.ammoInMag) ? state.ammoInMag : null,
      ammoReserve: Number.isFinite(state.ammoReserve) ? state.ammoReserve : null,
      isReloading: state.reloadTime > 0,
    };
  }

  getPreferredHudHand() {
    const preferred = this._preferredHudHand === 'left' ? 'left' : 'right';
    return this.hands?.[preferred] ? preferred : 'right';
  }

  getWeaponDpsInHand(hand = 'right') {
    const weapon = this.getHandWeapon(hand);
    if (!weapon?.runtime) return 0;
    const damage = Math.max(0, Number(weapon.runtime.baseDamage) || 0);
    const cooldown = Math.max(0.02, Number(weapon.runtime.cooldown) || 0.02);
    return damage / cooldown;
  }

  getCurrentWeaponDps() {
    return this.getWeaponDpsInHand(this.getPreferredHudHand());
  }

  _isMeleeWeapon(weapon) {
    return weapon?.category === WEAPON_CATEGORY.MELEE;
  }

  _canHandFire(hand) {
    const state = this.getHandState(hand);
    const weapon = this.getHandWeapon(hand);
    if (!weapon || state.reloadTime > 0 || state.fireCd > 0) return false;
    if (this._isMeleeWeapon(weapon) || this.infiniteAmmo) return true;
    return state.ammoInMag > 0;
  }

  setInfiniteAmmo(on) {
    this.infiniteAmmo = !!on;
    if (this.infiniteAmmo) {
      for (const hand of ['left', 'right']) {
        const state = this.getHandState(hand);
        state.reloadTime = 0;
        if (Number.isFinite(state.magCapacity)) state.ammoInMag = state.magCapacity;
      }
    }
  }

  setDebugHidden(on) {
    this._debugHidden = !!on;
    this.group.visible = !this._debugHidden;
  }

  isReloading(hand = null) {
    if (!hand) {
      return this.hands.left.reloadTime > 0 || this.hands.right.reloadTime > 0;
    }
    return this.getHandState(hand).reloadTime > 0;
  }

  startReload(hand) {
    const state = this.getHandState(hand);
    const weapon = this.getHandWeapon(hand);
    if (!weapon || this.infiniteAmmo || this._isMeleeWeapon(weapon)) return false;
    if (state.reloadTime > 0) return false;
    if (state.ammoInMag >= state.magCapacity || state.ammoReserve <= 0) return false;
    state.reloadTime = state.reloadDuration;
    if (this.audio) this.audio.play('reloadStart', { rate: 0.96, pitchJitter: 0.015 });
    return true;
  }

  finishReload(hand) {
    const state = this.getHandState(hand);
    const weapon = this.getHandWeapon(hand);
    if (!weapon || this._isMeleeWeapon(weapon)) return false;
    const missing = state.magCapacity - state.ammoInMag;
    const loaded = Math.min(missing, state.ammoReserve);
    state.ammoInMag += loaded;
    state.ammoReserve -= loaded;
    if (loaded > 0) this.audio?.play('reloadEnd', { rate: 1.08, pitchJitter: 0.02 });
    return loaded > 0;
  }

  addReserveAmmo(amount, hand = null) {
    if (amount <= 0) return 0;
    const hands = hand ? [hand] : ['left', 'right'];
    let remaining = amount;
    let gained = 0;
    for (const name of hands) {
      const state = this.getHandState(name);
      const weapon = this.getHandWeapon(name);
      if (!weapon || this._isMeleeWeapon(weapon) || !Number.isFinite(state.reserveCapacity)) continue;
      const before = state.ammoReserve;
      state.ammoReserve = Math.min(state.reserveCapacity, state.ammoReserve + remaining);
      const delta = state.ammoReserve - before;
      gained += delta;
      remaining = Math.max(0, remaining - delta);
      if (remaining <= 0) break;
    }
    return gained;
  }

  get ammoInMag() {
    const right = this.getHandState('right');
    return Number.isFinite(right.ammoInMag) ? right.ammoInMag : 0;
  }

  get ammoReserve() {
    const right = this.getHandState('right');
    return Number.isFinite(right.ammoReserve) ? right.ammoReserve : 0;
  }

  get reserveCapacity() {
    const right = this.getHandState('right');
    return Number.isFinite(right.reserveCapacity) ? right.reserveCapacity : 0;
  }

  getTotalReserveAmmo() {
    let total = 0;
    for (const hand of ['left', 'right']) {
      const state = this.getHandState(hand);
      if (Number.isFinite(state.ammoReserve)) total += state.ammoReserve;
    }
    return total;
  }

  getTotalReserveCapacity() {
    let total = 0;
    for (const hand of ['left', 'right']) {
      const state = this.getHandState(hand);
      if (Number.isFinite(state.reserveCapacity)) total += state.reserveCapacity;
    }
    return total;
  }

  equipWeaponInHand(hand, weaponId) {
    const weapon = getWeaponById(weaponId);
    if (!weapon) return false;
    this.hands[hand] = this._createHandState(weapon.id, null);
    return true;
  }

  hasEmptyHand() {
    return !this.hands.left?.weaponId || !this.hands.right?.weaponId;
  }

  tryAutoEquipWeapon(weaponId) {
    if (!this.hands.left?.weaponId) return this.equipWeaponInHand('left', weaponId);
    if (!this.hands.right?.weaponId) return this.equipWeaponInHand('right', weaponId);
    return false;
  }

  swapHandWeapon(hand, weaponId) {
    return this.equipWeaponInHand(hand, weaponId);
  }

  tryDash() {
    if (this.dashTime > 0 || this.stamina < STAMINA_DASH_COST) return false;
    const len = Math.hypot(this.vx, this.vz);
    let dx, dz;
    if (len > 0.1) { dx = this.vx / len; dz = this.vz / len; }
    else { dx = this.aimX; dz = this.aimZ; }
    this.dashDirX = dx;
    this.dashDirZ = dz;
    this.dashTime = DASH_TIME * this.dashIframeMul;
    this.stamina = Math.max(0, this.stamina - STAMINA_DASH_COST);
    this.dashCd = DASH_CD_BASE * (1 - this.stamina / STAMINA_MAX);
    this.invuln = Math.max(this.invuln, this.dashTime);
    if (this.audio) this.audio.play('dash');
    return true;
  }

  _updateFootsteps(dt) {
    if (!this.audio) return;
    const moving = Math.hypot(this.vx, this.vz) > 0.12;
    const groundedStride = moving && this.dashTime <= 0;
    if (!groundedStride) {
      this._footstepDistance = 0;
      return;
    }

    // Distance-based cadence keeps footsteps synced to movement speed.
    const speed = Math.hypot(this.vx, this.vz);
    this._footstepDistance += speed * dt;
    const strideLength = 2; // meters per footstep at default movement
    while (this._footstepDistance >= strideLength) {
      this._footstepDistance -= strideLength;
      this.audio.play('footstep', { pitchJitter: 0.02 });
    }
  }

  update(dt, input, walls, game) {
    if (!this.alive) {
      this.vx = 0;
      this.vz = 0;
      this._updateDeathSequence(dt);
      if (this.mixer) this.mixer.update(dt);
      return;
    }

    this.invuln = Math.max(0, this.invuln - dt);
    this._healBlinkTimer = Math.max(0, this._healBlinkTimer - dt);
    this._activeCd = Math.max(0, this._activeCd - dt);
    this._activeTime = Math.max(0, this._activeTime - dt);
    this.fireCd = Math.max(0, this.fireCd - dt);
    this._shootAnimTimer = Math.max(0, this._shootAnimTimer - dt);
    for (const hand of ['left', 'right']) {
      const state = this.getHandState(hand);
      state.fireCd = Math.max(0, state.fireCd - dt);
      if (state.reloadTime > 0) {
        state.reloadTime = Math.max(0, state.reloadTime - dt);
        if (state.reloadTime <= 0) this.finishReload(hand);
      }
    }
    this.getHandState('left').triggerHeld = !!input.mouse.leftDown;
    this.getHandState('right').triggerHeld = !!input.mouse.rightDown;
    if (!input.mouse.leftDown) this.getHandState('left').shotsSinceTrigger = 0;
    if (!input.mouse.rightDown) this.getHandState('right').shotsSinceTrigger = 0;

    const axis = input.moveAxis();
    const wantsRun = input.isDown('ShiftLeft') || input.isDown('ShiftRight');
    const moving = Math.hypot(axis.x, axis.z) > 0.01;
    const isRunning = wantsRun && moving && this.dashTime <= 0 && this.stamina > 0;
    const speed = this.moveSpeed(isRunning);
    this.vx = axis.x * speed;
    this.vz = axis.z * speed;

    if (isRunning) {
      this.stamina = Math.max(0, this.stamina - STAMINA_RUN_DRAIN_PER_SEC * dt);
    } else {
      const recovery = STAMINA_RECOVERY_PER_SEC / this.dashCdMul;
      this.stamina = Math.min(STAMINA_MAX, this.stamina + recovery * dt);
    }
    this.dashCd = DASH_CD_BASE * this.dashCdMul * (1 - this.stamina / STAMINA_MAX);

    // Void Shroud cloak accumulation
    if (this.hasFragment('void_shroud')) {
      const moving = Math.abs(axis.x) + Math.abs(axis.z) > 0.01;
      if (moving || this._cloaked && this.fireCd > 0) {
        if (this._cloaked && !moving) {
          // just fired; handled elsewhere
        }
      }
      if (moving) {
        this._standTime = 0;
        if (this._cloaked) { this._cloaked = false; }
      } else {
        this._standTime += dt;
        if (!this._cloaked && this._standTime >= 1.0) {
          this._cloaked = true;
          this.nextShotCritMul = 3;
        }
      }
    }

    let moveX, moveZ;
    if (this.dashTime > 0) {
      this.dashTime -= dt;
      moveX = this.dashDirX * DASH_SPEED * dt;
      moveZ = this.dashDirZ * DASH_SPEED * dt;
    } else {
      moveX = this.vx * dt;
      moveZ = this.vz * dt;
    }

    const nextX = this.x + moveX;
    const nextZ = this.z + moveZ;
    const resolved = resolveCircleWalls(nextX, nextZ, this.radius, walls);
    this.x = resolved.x;
    this.z = resolved.z;

    // Aim
    const aimDx = input.mouse.worldX - this.x;
    const aimDz = input.mouse.worldZ - this.z;
    const aimLen = Math.hypot(aimDx, aimDz);
    if (aimLen > 0.01) {
      this.aimX = aimDx / aimLen;
      this.aimZ = aimDz / aimLen;
    }

    // Fire
    if (input.consumeKey('KeyR')) {
      this.startReload('left');
      this.startReload('right');
    }

    if (input.mouse.leftDown && !input.mouse.rightDown) this._preferredHudHand = 'left';
    else if (input.mouse.rightDown && !input.mouse.leftDown) this._preferredHudHand = 'right';

    if (input.mouse.leftDown) this._fireHand(game, 'left');
    if (input.mouse.rightDown) this._fireHand(game, 'right');
    if (input.consumeKey(ACTIVE_KEY)) this._triggerActive(game);

    // Dash
    if (input.consumeSpace()) this.tryDash();
    this._updateFootsteps(dt);

    this.group.position.set(this.x, 0, this.z);

    // Facing: body faces movement, upper body faces aim
    if (this._upperBody) {
      const aimAngle = Math.atan2(this.aimX, this.aimZ);
      this._upperBody.rotation.y = aimAngle;
      this._meleeFaceTimer = Math.max(0, this._meleeFaceTimer - dt);
      if (this._meleeFaceTimer > 0) {
        this.group.rotation.y = aimAngle;
      } else if (Math.abs(this.vx) + Math.abs(this.vz) > 0.1) {
        const moveAngle = Math.atan2(this.vx, this.vz);
        // Gentle body lean
        this.group.rotation.y = moveAngle * 0.3 + aimAngle * 0.7;
      } else {
        this.group.rotation.y = aimAngle;
      }
    } else if (this._model) {
      const aimAngle = Math.atan2(this.aimX, this.aimZ);
      this._model.rotation.y = aimAngle;
    }

    // Animation state
    if (this.mixer) {
      this.mixer.update(dt);
      const moving = Math.abs(this.vx) + Math.abs(this.vz) > 0.1;
      const desired = this._shootAnimTimer > 0 && this._shootAction
        ? this._shootAction
        : (moving
          ? (this._pickDirectionalRunAction() || this._findAction(['run', 'walk']) || this._currentAction)
          : (this._idleAction || this._findAction(['idle', 'breathing']) || this._currentAction));
      this._playAction(desired);
    }

    // Invuln flicker
    if (this._model) {
      this._model.visible = this.invuln > 0 ? Math.floor(this.invuln * 24) % 2 === 0 : true;
    }
    if (this.characterId === 'kira') {
      const isVanished = this._activeTime > 0;
      this._cloaked = isVanished || this._cloaked;
      if (this._model) this._model.visible = isVanished ? Math.floor(performance.now() / 60) % 2 === 0 : this._model.visible;
    }
    this._applyHealBlinkVisual();
  }

  _applyHealBlinkVisual() {
    const blinkActive = this._healBlinkTimer > 0 && Math.floor(this._healBlinkTimer * 34) % 2 === 0;
    const emissiveIntensity = blinkActive ? 0.85 : 0;
    const tintColor = blinkActive ? 0xffffff : null;
    this.group.traverse((node) => {
      if (!node?.isMesh) return;
      const mats = Array.isArray(node.material) ? node.material : [node.material];
      for (const mat of mats) {
        if (!mat || !('emissiveIntensity' in mat) || !mat.emissive) continue;
        mat.emissiveIntensity = emissiveIntensity;
        if (tintColor !== null) mat.emissive.setHex(tintColor);
      }
    });
  }
  _triggerActive(game) {
    if (this._activeCd > 0) return false;
    if (this.characterId === 'kira') {
      this._activeTime = 2;
      this._activeCd = 12;
      this._vanishBreakBonus = 2;
      game?.toast?.('Vanish');
      return true;
    }
    if (this.characterId === 'dax') {
      this._activeTime = 2;
      this._activeCd = 15;
      game?.toast?.('Bulwark');
      return true;
    }
    if (this.characterId === 'yael') {
      this._activeCd = 18;
      for (const enemy of game?.enemies || []) {
        const d = Math.hypot(enemy.x - this.x, enemy.z - this.z);
        if (d <= 8) enemy.fireCd = Math.max(enemy.fireCd, 1.5);
      }
      game?.toast?.('Pulse Burst');
      return true;
    }
    if (this.characterId === 'rook') {
      this._activeCd = 20;
      this._markedEnemy = this._findClosestEnemy(game, 14);
      if (this._markedEnemy) game?.toast?.('Target Marked');
      return !!this._markedEnemy;
    }
    if (this.characterId === 'esme') {
      this._activeCd = 22;
      const hit = game?.room?.breachAt?.(this.x + this.aimX * 2, this.z + this.aimZ * 2, 1.4);
      if (hit) game?.toast?.('Breach Charge');
      return !!hit;
    }
    return false;
  }
  _findClosestEnemy(game, maxDist) {
    let best = null;
    let bestD = Infinity;
    for (const enemy of game?.enemies || []) {
      if (!enemy?.alive) continue;
      const d = Math.hypot(enemy.x - this.x, enemy.z - this.z);
      if (d < bestD && d <= maxDist) {
        best = enemy;
        bestD = d;
      }
    }
    return best;
  }

  _critMultiplierAndConsume() {
    let critMul = 1;
    if (this._cloaked) {
      critMul = this.nextShotCritMul;
      this._cloaked = false;
      this._standTime = 0;
      this.nextShotCritMul = 1;
    }
    return critMul;
  }

  _rollCrit(weapon) {
    const chance = weapon?.runtime?.critChance ?? 0;
    return Math.random() < chance;
  }

  _meleeRangeForWeapon(weapon) {
    return BASE_MELEE_REACH + (weapon.stats.range / 100) * 2.6;
  }

  _performMeleeAttack(game, hand, weapon, critMul) {
    this._meleeFaceTimer = 0.14;
    game?.spawnMeleeSlashFx?.({
      weapon,
      hand,
      player: this,
      aimX: this.aimX,
      aimZ: this.aimZ,
      when: 0.2,
    });
    const damageBase = weapon.runtime.baseDamage * critMul;
    const isCrit = this._rollCrit(weapon);
    const damage = damageBase * (isCrit ? 1.6 : 1);
    const reach = this._meleeRangeForWeapon(weapon);
    const coneDot = weapon.id === 'tetsu_kusarigama' ? 0.3 : 0.5;
    for (const enemy of game.enemies) {
      if (!enemy?.alive) continue;
      const dx = enemy.x - this.x;
      const dz = enemy.z - this.z;
      const dist = Math.hypot(dx, dz);
      if (dist > reach + enemy.radius) continue;
      const nx = dx / Math.max(0.0001, dist);
      const nz = dz / Math.max(0.0001, dist);
      const dot = nx * this.aimX + nz * this.aimZ;
      if (dot < coneDot) continue;
      enemy.queueHitDirection?.(this.aimX, this.aimZ);
      enemy.takeDamage(damage, game);
      if (weapon.id === 'oni_blade') {
        const healAmount = Math.min(30, Math.ceil(damage * 0.12));
        if (healAmount > 0) this.heal(healAmount);
      }
    }
  }

  _fireProjectileWeapon(game, hand, weapon, critMul, { consumeAmmo = true } = {}) {
    const state = this.getHandState(hand);
    const muzzleX = this.x + this.aimX * 0.9;
    const muzzleZ = this.z + this.aimZ * 0.9;
    let spread = (weapon.runtime.spread || 0) * (hand === 'left' ? 1.08 : 1);
    if (weapon.id === 'shinkansen_pistol') {
      // Make first shot of each trigger hold perfectly accurate, then widen rapidly.
      spread = state.shotsSinceTrigger === 0 ? 0 : spread * 2.6;
    }
    const angleJitter = (Math.random() * 2 - 1) * spread;
    const baseAngle = Math.atan2(this.aimX, this.aimZ) + angleJitter;
    const dirX = Math.sin(baseAngle);
    const dirZ = Math.cos(baseAngle);
    const isCrit = this._rollCrit(weapon);
    let damage = weapon.runtime.baseDamage * critMul * (this._damageMul || 1);
    if (this.characterId === 'kira' && this._vanishBreakBonus > 1) {
      damage *= this._vanishBreakBonus;
      this._vanishBreakBonus = 1;
      this._activeTime = 0;
    }
    if (this.characterId === 'rook') {
      const now = performance.now() / 1000;
      if (now - this._lastShotAt > 1.5) damage *= 1.4;
      this._lastShotAt = now;
    }
    if (isCrit) damage *= 1.6;
    let speed = weapon.runtime.projectileSpeed;
    let life = weapon.runtime.projectileLife;

    const opts = {
      speed,
      damage,
      life,
      sourceWeaponId: weapon.id,
    };

    if (weapon.id === 'shinkansen_pistol') opts.maxBounces = 2;
    if (!opts.maxBounces && this.hasFragment('ricochet')) opts.maxBounces = 1;
    if (weapon.id === 'reiketsu_shotgun' && isCrit) {
      opts.bleedDps = 5;
      opts.bleedDuration = 3;
    }
    if (weapon.id === 'jinsoku_smg' && this.getHandWeapon(hand === 'left' ? 'right' : 'left')?.id === 'jinsoku_smg') {
      opts.damage *= 0.8;
      spread *= 1.3;
    }
    if (weapon.id === 'jigoku_launcher' || weapon.id === 'tenrai_mortar') {
      opts.explosionRadius = weapon.id === 'jigoku_launcher' ? 2.7 : 3.1;
      opts.explosionDamage = opts.damage * 0.9;
      if (weapon.id === 'jigoku_launcher') opts.clusterCount = 6;
      if (weapon.id === 'tenrai_mortar') opts.delayedFuse = 0.7;
      speed *= weapon.id === 'tenrai_mortar' ? 0.7 : 0.86;
      life *= weapon.id === 'tenrai_mortar' ? 1.25 : 1.05;
      opts.speed = speed;
      opts.life = life;
    }

    game.spawnPlayerProjectile(muzzleX, muzzleZ, dirX, dirZ, opts);
    if (this.characterId === 'rook' && this._markedEnemy?.alive) {
      const m = this._markedEnemy;
      const dMark = Math.hypot(m.x - muzzleX, m.z - muzzleZ);
      if (dMark < 1.3) {
        m.takeDamage(damage * 2, game);
        m.fireCd = Math.max(m.fireCd, 2);
        this._markedEnemy = null;
      }
    }

    // Keep fragment behavior active across all ranged projectile weapons.
    this.shotCounter++;
    const echo = this.hasFragment('echo_shot') && this.shotCounter % 3 === 0;
    if (echo) {
      const rings = 12;
      for (let i = 0; i < rings; i++) {
        const a = (i / rings) * Math.PI * 2;
        const dx = Math.cos(a);
        const dz = Math.sin(a);
        game.spawnPlayerProjectile(muzzleX, muzzleZ, dx, dz, {
          speed: ECHO_RING_BULLET_SPEED,
          life: 0.55,
          damage: 1,
          echo: true,
        });
      }
    }

    if (consumeAmmo && !this.infiniteAmmo && Number.isFinite(state.ammoInMag)) state.ammoInMag -= 1;
    state.shotsSinceTrigger += 1;
  }

  _fireMineWeapon(game, hand, weapon, critMul) {
    const damage = weapon.runtime.baseDamage * critMul;
    game.spawnPlayerMine(this.x + this.aimX * 1.1, this.z + this.aimZ * 1.1, {
      sourceWeaponId: weapon.id,
      damage,
      radius: 2.8,
      triggerRadius: 1.35,
      maxLifetime: 10,
    });
    const state = this.getHandState(hand);
    if (!this.infiniteAmmo && Number.isFinite(state.ammoInMag)) state.ammoInMag -= 1;
  }

  _fireHand(game, hand) {
    const state = this.getHandState(hand);
    const weapon = this.getHandWeapon(hand);
    if (!weapon || state.fireCd > 0) return false;
    if (!this._canHandFire(hand)) {
      if (!this.infiniteAmmo && state.ammoInMag <= 0 && !this.isReloading(hand)) {
        if (!this.startReload(hand)) this.audio?.play('emptyShot', { pitchJitter: 0.04 });
      }
      state.fireCd = 0.12;
      return false;
    }

    const critMul = this._critMultiplierAndConsume();
    if (this._isMeleeWeapon(weapon)) {
      this._performMeleeAttack(game, hand, weapon, critMul);
    } else if (weapon.id === 'bakuha_mines') {
      this._fireMineWeapon(game, hand, weapon, critMul);
    } else if (weapon.id === 'stormcaller_smr2') {
      for (let i = 0; i < 3; i++) {
        const delay = i * 0.03;
        game.schedulePlayerBurstShot(delay, () => {
          this._fireProjectileWeapon(game, hand, weapon, critMul, { consumeAmmo: false });
        });
      }
      if (!this.infiniteAmmo && Number.isFinite(state.ammoInMag)) state.ammoInMag = Math.max(0, state.ammoInMag - 1);
    } else {
      this._fireProjectileWeapon(game, hand, weapon, critMul);
    }

    const isMelee = this._isMeleeWeapon(weapon);
    const fireSfx = isMelee
      ? 'meleeShot'
      : (weapon.id === 'jigoku_launcher' || weapon.id === 'tenrai_mortar' ? 'launcherFire' : 'shot');
    this.audio?.play(fireSfx, { pitchJitter: 0.05 });
    this._triggerShootAnimation();
    state.fireCd = this._weaponCooldown(weapon);
    this.fireCd = Math.max(this.fireCd, state.fireCd);
    this._preferredHudHand = hand;
    if (!this.infiniteAmmo && state.ammoInMag <= 0) this.startReload(hand);
    return true;
  }

  onKill(game) {
    if (this.hasFragment('reapers_mark') && Math.random() < 0.25) {
      this.heal(1);
      game?.toast('+1 HP');
    }
    if (this.hasFragment('fracture_core') && Math.random() < 0.2) {
      // spawn explosive shard handled by game
      return { explosive: true };
    }
    return null;
  }

  onLevelStart() {
    if (!this.audio) return;
    this.audio.play('playerLevelStart', { pitchJitter: 0.03 });
  }

  reset(x = 0, z = 0) {
    this.x = x; this.z = z;
    this.hp = this.maxHp;
    this.medkits = STARTER_MEDKITS;
    this.alive = true;
    this.dashCd = 0;
    this.dashTime = 0;
    this.invuln = 0;
    this.fireCd = 0;
    this.stamina = STAMINA_MAX;
    this._applyStarterLoadout(this._characterProfile);
    this.swapHintShown = false;
    this.infiniteAmmo = false;
    this._preferredHudHand = 'right';
    this.shotCounter = 0;
    this._standTime = 0;
    this._cloaked = false;
    this.fragments.clear();
    this.speedMul = 1;
    this.fireRateMul = 1;
    this.dashCdMul = 1;
    this.dashIframeMul = 1;
    this.nextShotCritMul = 1;
    this._footstepDistance = 0;
    this._meleeFaceTimer = 0;
    this._activeCd = 0;
    this._activeTime = 0;
    this._roomArmorReady = this.characterId === 'dax';
    this._lastShotAt = -999;
    this._markedEnemy = null;
    this._vanishBreakBonus = 1;
    this._healBlinkTimer = 0;
    this._deathSequence.active = false;
    this._deathSequence.elapsed = 0;
    this._deathSequence.animDone = false;
    this._deathSequence.startScale.set(1, 1, 1);
    this._deathSequence.startRotX = 0;
    this.group.rotation.x = 0;
    this.group.scale.set(1, 1, 1);
    this._clearDeathBloodParticles();
    this.setDebugHidden(false);
    if (this._model) this._model.visible = true;
    this.group.position.set(x, 0, z);
  }
}
